// Shared PDF page chrome: Lens logo (top-right), bottom line, and confidential footer.

import { jsPDF } from 'jspdf';

const LOGO_SRC = '/assets/logo/Lens_Logo_Blue.png';

export const PDF_CONFIDENTIAL_FOOTER =
    'CONFIDENTIAL | For intended recipient(s) only. This document contains confidential and proprietary information and must not be disclosed, copied, distributed, or reproduced without prior authorisation.';

/** Standard 1" margins (72pt) and landscape A4 — used by all PDF exports. */
export const PDF_PAGE_LAYOUT = {
    format: 'a4',
    unit: 'pt',
    orientation: 'landscape',
    marginX: 72,
    marginY: 72,
};

export const PDF_PAGE_CHROME = {
    primaryColor: [27, 33, 73],
    primaryHex: '#1b2149',
    lineWidth: 0.5,
    marginX: PDF_PAGE_LAYOUT.marginX,
    logoWidthPt: 52,
    logoAspect: 128 / 352,
    logoTopY: 20,
    contentGapBelowLogo: 14,
    footerFontSize: 7,
    footerTextColor: [107, 111, 137],
    footerLineGap: 5,
    footerBottomPadding: 16,
    contentGapAboveBottomLine: 14,
};

export const ptToCssPx = pt => Math.round(pt * (96 / 72));

export const getPdfContentWidthPt = (pageWidth, marginX = PDF_PAGE_CHROME.marginX) =>
    pageWidth - marginX * 2;

export const createPdfDocument = () =>
    new jsPDF({
        orientation: PDF_PAGE_LAYOUT.orientation,
        unit: PDF_PAGE_LAYOUT.unit,
        format: PDF_PAGE_LAYOUT.format,
    });

export const addPdfPage = doc => {
    doc.addPage(
        PDF_PAGE_LAYOUT.format,
        PDF_PAGE_LAYOUT.orientation === 'landscape' ? 'l' : 'p',
    );
};

let cachedLogoDataUrl = null;
let logoLoadPromise = null;

const imageSrcToPngDataUrl = async src => {
    const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load Lens logo'));
        img.src = src;
    });

    const width = image.naturalWidth || 352;
    const height = image.naturalHeight || 128;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Unable to create canvas for Lens logo');
    }
    ctx.drawImage(image, 0, 0, width, height);
    return canvas.toDataURL('image/png');
};

export const getLensLogoDataUrl = async () => {
    if (cachedLogoDataUrl) return cachedLogoDataUrl;
    if (!logoLoadPromise) {
        logoLoadPromise = imageSrcToPngDataUrl(LOGO_SRC)
            .then(dataUrl => {
                cachedLogoDataUrl = dataUrl;
                return cachedLogoDataUrl;
            })
            .catch(error => {
                logoLoadPromise = null;
                throw error;
            });
    }
    return logoLoadPromise;
};

export const getPdfLogoDimensions = () => {
    const { logoWidthPt, logoAspect } = PDF_PAGE_CHROME;
    return {
        width: logoWidthPt,
        height: logoWidthPt * logoAspect,
    };
};

const footerMetricsCache = new Map();

const applyPdfFooterFont = doc => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(PDF_PAGE_CHROME.footerFontSize);
};

/** Compute wrapped footer lines once so every page uses identical layout. */
const getPdfFooterMetrics = (doc, marginX = PDF_PAGE_CHROME.marginX) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const cacheKey = `${pageWidth}:${marginX}:${PDF_PAGE_CHROME.footerFontSize}`;

    if (footerMetricsCache.has(cacheKey)) {
        return footerMetricsCache.get(cacheKey);
    }

    const textWidth = pageWidth - marginX * 2;
    const { footerFontSize, footerLineGap, footerBottomPadding, lineWidth } = PDF_PAGE_CHROME;

    const scratch = new jsPDF({
        orientation: PDF_PAGE_LAYOUT.orientation,
        unit: PDF_PAGE_LAYOUT.unit,
        format: PDF_PAGE_LAYOUT.format,
    });
    applyPdfFooterFont(scratch);
    const lines = scratch.splitTextToSize(PDF_CONFIDENTIAL_FOOTER, textWidth);

    const lineHeight = footerFontSize * 1.35;
    const textHeight = lines.length * lineHeight;
    const totalHeight = footerBottomPadding + textHeight + footerLineGap + lineWidth;

    const metrics = { lines, textWidth, lineHeight, totalHeight };
    footerMetricsCache.set(cacheKey, metrics);
    return metrics;
};

/** Content-safe insets after accounting for page chrome. */
export const getPdfContentInsets = ({ marginX = PDF_PAGE_CHROME.marginX, doc } = {}) => {
    const { logoTopY, logoWidthPt, logoAspect, contentGapBelowLogo, contentGapAboveBottomLine } =
        PDF_PAGE_CHROME;
    const logoHeight = logoWidthPt * logoAspect;
    const contentTop = logoTopY + logoHeight + contentGapBelowLogo;
    const footerChromeHeight = doc ? getPdfFooterMetrics(doc, marginX).totalHeight : 46;

    return {
        marginX,
        marginY: marginX,
        contentTop,
        contentBottom: footerChromeHeight + contentGapAboveBottomLine,
    };
};

/** Draw logo (top-right), bottom line, and confidential footer text. */
export const drawPdfPageChrome = (doc, logoDataUrl, { marginX = PDF_PAGE_CHROME.marginX } = {}) => {
    if (!doc) return;

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const {
        lineWidth,
        logoWidthPt,
        logoAspect,
        logoTopY,
        footerFontSize,
        footerTextColor,
        footerLineGap,
    } = PDF_PAGE_CHROME;

    if (logoDataUrl) {
        const logoHeight = logoWidthPt * logoAspect;
        const logoX = pageWidth - marginX - logoWidthPt;
        doc.addImage(logoDataUrl, 'PNG', logoX, logoTopY, logoWidthPt, logoHeight);
    }

    const footerMetrics = getPdfFooterMetrics(doc, marginX);
    const bottomLineY = pageHeight - footerMetrics.totalHeight;

    doc.setDrawColor(...footerTextColor);
    doc.setLineWidth(lineWidth);
    doc.line(marginX, bottomLineY, pageWidth - marginX, bottomLineY);

    applyPdfFooterFont(doc);
    doc.setTextColor(...footerTextColor);

    const textStartY = bottomLineY + footerLineGap + footerFontSize * 0.85;
    footerMetrics.lines.forEach((line, index) => {
        doc.text(line, marginX, textStartY + index * footerMetrics.lineHeight);
    });
};
